/**
 * Bloc "Suivi de chantier" affiché sur PublicQuotePage (portail client).
 *
 * Sections conditionnelles selon `chantier_status` :
 *  - `a_planifier` : "Prochainement — votre planning".
 *  - `en_cours`    : étapes (highlight étape courante) + fil client (photos, anté-chrono).
 *  - `finalise`    : bloc "Récap final à signer" en top + reste visible.
 *  - `termine`     : bandeau clôture (date) + accès permanent.
 *
 * Palette espace client : fond #F7F3EC, texte #2A2420, accent = accent_color de la company.
 */
import { useState, useRef } from "react";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { Loader2, Download, PenLine, CheckCircle2, Hammer, PartyPopper, ChevronDown, ChevronUp } from "lucide-react";
import { publicApiError } from "@/lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-BE", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("fr-BE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

const STEP_META = {
  pending: { bg: "#E5DDD1", text: "#2A2420" },
  current: { bg: "#D99A2B", text: "#F7F3EC" },
  done: { bg: "#5A6338", text: "#F7F3EC" },
};

export default function PublicChantierBlock({ token, accent, chantierSteps, chantierUpdates, chantier, onRecapSigned, companyName }) {
  const [visibleUpdates, setVisibleUpdates] = useState(5);
  const [showRecapSign, setShowRecapSign] = useState(false);
  const [recapSignerName, setRecapSignerName] = useState("");
  const [recapCgvOk, setRecapCgvOk] = useState(false);
  const [recapSubmitting, setRecapSubmitting] = useState(false);
  const [recapSignedLocal, setRecapSignedLocal] = useState(false);
  const [recapError, setRecapError] = useState("");
  const recapSigRef = useRef(null);

  if (!chantierSteps) return null;
  const status = chantierSteps.chantier_status;
  const steps = chantierSteps.steps || [];
  const updates = chantierUpdates || [];
  const recapAlreadySigned = chantier?.recap_signed_at || recapSignedLocal;

  const signRecap = async () => {
    if (!recapSigRef.current || recapSigRef.current.isEmpty()) {
      setRecapError("Merci de dessiner votre signature.");
      return;
    }
    if (recapSignerName.trim().length < 2) {
      setRecapError("Le nom du signataire est obligatoire.");
      return;
    }
    if (!recapCgvOk) {
      setRecapError("Merci d'accepter les CGV.");
      return;
    }
    setRecapSubmitting(true); setRecapError("");
    try {
      const png = recapSigRef.current.toDataURL("image/png").split(",")[1];
      const res = await axios.post(
        `${API_BASE}/api/public/chantier/${token}/recap/sign`,
        {
          signature_png_base64: png,
          signer_name: recapSignerName.trim(),
          cgv_accepted: recapCgvOk,
        },
        { timeout: 30000 },
      );
      if (res.data.ok) {
        setRecapSignedLocal(true);
        setShowRecapSign(false);
        onRecapSigned?.();
      }
    } catch (e) {
      // Commit 2 hardening (2026-09-21) — Message métier public, fallback
      // stable jamais alimenté par le backend (particulier client final).
      // Détail technique en console via publicApiError. `companyName` reçu
      // en prop depuis PublicQuotePage (fallback "votre entreprise" si
      // absent → rétrocompat).
      const contact = companyName ? `contactez ${companyName}` : "contactez votre entreprise";
      setRecapError(
        publicApiError(
          e,
          `La signature n'a pas pu être enregistrée. Réessayez dans un instant. Si le problème persiste, ${contact}.`,
        ),
      );
    } finally { setRecapSubmitting(false); }
  };

  // ---- Sub-blocks ----

  const StepsBlock = () => (
    <section className="rounded-lg border p-4" style={{ borderColor: "#E5DDD1", background: "#FFFFFF" }} data-testid="public-chantier-steps">
      <h3 className="mb-3 inline-flex items-center gap-2 text-base font-semibold" style={{ color: accent }}>
        <Hammer className="h-4 w-4" /> Avancement du chantier
      </h3>
      <ol className="space-y-2">
        {steps.map((s, i) => {
          const meta = STEP_META[s.status] || STEP_META.pending;
          return (
            <li key={s.id} className="flex items-start gap-3" data-testid={`public-step-${s.id}`}>
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                   style={{ background: meta.bg, color: meta.text }}>
                {s.status === "done" ? "✓" : i + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className={`font-semibold ${s.status === "current" ? "text-[#A8751C]" : ""}`}>
                  {s.title}
                  {s.status === "current" && <span className="ml-2 rounded px-1.5 py-0.5 text-[10px] uppercase" style={{ background: "#D99A2B", color: "#F7F3EC" }}>En cours</span>}
                </div>
                {s.description && <div className="text-xs opacity-70">{s.description}</div>}
                {s.status === "done" && s.completed_at && (
                  <div className="text-xs" style={{ color: "#5A6338" }}>Terminée le {fmtDate(s.completed_at)}</div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );

  const UpdatesBlock = () => (
    <section className="rounded-lg border p-4" style={{ borderColor: "#E5DDD1", background: "#FFFFFF" }} data-testid="public-chantier-updates">
      <h3 className="mb-3 inline-flex items-center gap-2 text-base font-semibold" style={{ color: accent }}>
        Actualités du chantier
      </h3>
      {updates.length === 0 ? (
        <p className="text-sm opacity-60">Pas encore d'actualité publiée.</p>
      ) : (
        <>
          <ul className="space-y-3">
            {updates.slice(0, visibleUpdates).map((u) => (
              <li key={u.id} className="rounded border p-3" style={{ borderColor: "#F0E9DC", background: "#FDFAF3" }} data-testid={`public-update-${u.id}`}>
                <div className="mb-1 text-xs opacity-60">{fmtDateTime(u.created_at)}</div>
                <p className="whitespace-pre-wrap text-sm">{u.content}</p>
                {u.photos && u.photos.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {u.photos.map((p, i) => (
                      <a key={i} href={p.url || p} target="_blank" rel="noopener noreferrer">
                        <img src={p.url || p} alt="" loading="lazy"
                             className="h-20 w-full rounded object-cover" style={{ background: "#F0E9DC" }} />
                      </a>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {visibleUpdates < updates.length && (
            <button
              onClick={() => setVisibleUpdates(visibleUpdates + 10)}
              className="mt-3 flex items-center gap-1 text-sm underline opacity-70"
              data-testid="public-updates-load-more"
            >
              <ChevronDown className="h-3 w-3" /> Voir plus ({updates.length - visibleUpdates} restant(s))
            </button>
          )}
        </>
      )}
    </section>
  );

  const RecapSignBlock = () => (
    <section
      className="rounded-lg border-2 p-4 sm:p-6"
      style={{ borderColor: accent, background: "#FFFFFF" }}
      data-testid="public-recap-sign-block"
    >
      <h3 className="mb-2 inline-flex items-center gap-2 text-lg font-bold" style={{ color: accent }}>
        <PenLine className="h-5 w-5" /> Récap final à signer
      </h3>
      <p className="mb-4 text-sm">
        Votre chantier est finalisé. Merci de signer le récapitulatif final pour clôturer votre dossier.
        Vous pourrez ensuite télécharger le récap PDF signé à tout moment.
      </p>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <a
          href={`${API_BASE}/api/public/chantier/${token}/recap/pdf`}
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold"
          style={{ borderColor: accent, color: accent }}
          data-testid="public-recap-preview-pdf"
        >
          <Download className="h-4 w-4" /> Prévisualiser le récap PDF
        </a>
        {!showRecapSign && (
          <button
            onClick={() => setShowRecapSign(true)}
            className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold"
            style={{ background: accent, color: "#F7F3EC" }}
            data-testid="public-recap-sign-open"
          >
            <PenLine className="h-4 w-4" /> Signer le récap
          </button>
        )}
      </div>
      {showRecapSign && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-semibold mb-1">Nom complet du signataire *</label>
            <input
              type="text" value={recapSignerName}
              onChange={(e) => setRecapSignerName(e.target.value)}
              className="w-full rounded border px-3 py-2 text-sm"
              style={{ borderColor: "#E5DDD1", background: "#FDFAF3", color: "#2A2420" }}
              data-testid="public-recap-signer-name"
            />
          </div>
          <label className="flex items-start gap-2 text-sm" data-testid="public-recap-cgv">
            <input type="checkbox" checked={recapCgvOk} onChange={(e) => setRecapCgvOk(e.target.checked)} className="mt-0.5" />
            <span>Je confirme que les travaux sont réalisés conformément et j'accepte le récap comme final *</span>
          </label>
          <div>
            <div className="mb-1 text-sm font-semibold">Signature *</div>
            <div className="rounded border" style={{ borderColor: "#E5DDD1", background: "#FDFAF3" }}>
              <SignatureCanvas
                ref={recapSigRef} penColor="#2A2420"
                canvasProps={{ className: "w-full", height: 160, "data-testid": "public-recap-canvas" }}
              />
            </div>
            <button onClick={() => recapSigRef.current?.clear()}
                    className="mt-1 text-xs underline opacity-70" data-testid="public-recap-clear">
              Effacer
            </button>
          </div>
          {recapError && (
            <div className="rounded p-2 text-sm" style={{ background: "#FBE7E1", color: "#8B2E1F" }} data-testid="public-recap-error">
              {recapError}
            </div>
          )}
          <button
            onClick={signRecap} disabled={recapSubmitting}
            className="w-full rounded-lg py-3 text-sm font-semibold disabled:opacity-50"
            style={{ background: accent, color: "#F7F3EC" }}
            data-testid="public-recap-sign-submit"
          >
            {recapSubmitting ? "Signature en cours…" : "Signer et clôturer"}
          </button>
        </div>
      )}
    </section>
  );

  const RecapSignedBlock = () => (
    <section
      className="rounded-lg p-4"
      style={{ background: "#F4F8EC", border: "2px solid #5A6338" }}
      data-testid="public-recap-signed-block"
    >
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-5 w-5" style={{ color: "#5A6338" }} />
        <span className="font-semibold" style={{ color: "#5A6338" }}>Récap final signé</span>
      </div>
      {chantier?.recap_signed_at && (
        <p className="mt-1 text-xs opacity-70">Signé le {fmtDate(chantier.recap_signed_at)} par {chantier.recap_signer_name}</p>
      )}
      <a
        href={`${API_BASE}/api/public/chantier/${token}/recap/pdf`}
        target="_blank" rel="noopener noreferrer"
        className="mt-3 inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm"
        style={{ background: "#5A6338", color: "#F7F3EC" }}
        data-testid="public-recap-signed-pdf"
      >
        <Download className="h-3 w-3" /> Télécharger le récap signé
      </a>
    </section>
  );

  // ---- Layout par statut ----
  if (status === "a_planifier") {
    return (
      <section className="mt-6 rounded-lg border p-6 text-center"
               style={{ borderColor: "#E5DDD1", background: "#FFFFFF" }}
               data-testid="public-chantier-planning">
        <Hammer className="mx-auto h-10 w-10" style={{ color: accent }} />
        <h3 className="mt-2 text-lg font-semibold">Prochainement : votre planning</h3>
        <p className="mt-2 text-sm opacity-70">
          Votre chantier est en cours de planification. Vous recevrez un email dès que les étapes seront confirmées.
        </p>
      </section>
    );
  }

  if (status === "en_cours") {
    return (
      <div className="mt-6 space-y-4">
        <StepsBlock />
        <UpdatesBlock />
      </div>
    );
  }

  if (status === "finalise") {
    return (
      <div className="mt-6 space-y-4">
        {recapAlreadySigned ? <RecapSignedBlock /> : <RecapSignBlock />}
        <StepsBlock />
        <UpdatesBlock />
      </div>
    );
  }

  if (status === "termine") {
    return (
      <div className="mt-6 space-y-4">
        <section className="rounded-lg p-5 text-center"
                 style={{ background: "#5A6338", color: "#F7F3EC" }}
                 data-testid="public-chantier-closed-banner">
          <PartyPopper className="mx-auto h-8 w-8" />
          <h3 className="mt-2 text-lg font-bold">Chantier clôturé</h3>
          {chantier?.closed_at && (
            <p className="text-sm opacity-90">Terminé le {fmtDate(chantier.closed_at)} — Merci pour votre confiance.</p>
          )}
        </section>
        {recapAlreadySigned && <RecapSignedBlock />}
        <StepsBlock />
        {updates.length > 0 && <UpdatesBlock />}
      </div>
    );
  }

  return null;
}
